import { useEffect, useState } from 'react';
import {
  amount,
  billNumberOf,
  downloadReceipt,
  isEdited,
  isVoided,
  itemCodeOf,
  itemTitleOf,
  itemTotalOf,
  itemUnitPriceOf,
  paymentSummary,
  pendingCancelRequestOf,
  posApi,
  printReceipt,
  refusedCancelRequestOf,
  saleCouponOf,
  saleDiscountOf,
  saleSubtotalOf,
  saleTotalOf,
  tillEditState,
  type PosSale,
} from '../api/pos';
import { errorMessage } from '../api/client';
import { billDateTime, money } from '../format';
import { useAuth } from '../auth/AuthContext';
import EditBill from './EditBill';
import RequestCancel from './RequestCancel';

const STORE_NAME = import.meta.env.VITE_STORE_NAME || 'Blossom Books';
const STORE_ADDRESS = import.meta.env.VITE_STORE_ADDRESS || 'MG Road, Ernakulam';
const STORE_PHONE = import.meta.env.VITE_STORE_PHONE || '9895648013, 8138823058';
const STORE_GSTIN = import.meta.env.VITE_STORE_GSTIN || '';

/**
 * The bill, on screen for staff to check against what the customer is
 * holding.
 *
 * Paper does NOT come from this markup. Both buttons ask the server for a
 * rendered PDF — an 80mm one for the till roll, an A4 one for a customer who
 * wants a copy — because the paper width then comes out right every time
 * instead of depending on the browser's print CSS and page setup.
 */
export default function InvoiceView({
  sale,
  onClose,
  onEdited,
}: {
  sale: PosSale;
  onClose: () => void;
  /**
   * Called with the edited bill once staff save a change here.
   *
   * Its presence is also what decides whether editing is offered at all:
   * the screen showing the bill has to be able to take the new one back, or
   * the list behind this dialog would still be showing the old total. The
   * billing screen passes nothing — the bill it shows was raised seconds ago
   * and the way to fix it is the till itself, not an edit on the record.
   */
  onEdited?: (updated: PosSale) => void;
}) {
  const [busy, setBusy] = useState<'print' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [requestingCancel, setRequestingCancel] = useState(false);
  // Withdrawing asks once more inline — it is one tap from the print button.
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The edit and cancel dialogs sit on top and handle their own Escape.
      // Without this guard one press would close both, throwing away a
      // half-typed edit along with the bill behind it.
      if (e.key === 'Escape' && !editing && !requestingCancel) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing, requestingCancel]);

  const items = sale.items ?? [];
  const coupon = saleCouponOf(sale);
  const allOff = saleDiscountOf(sale);
  // The counter's own share; the coupon gets its own row, as on the receipt.
  const discount = Math.round((allOff - (coupon?.amount ?? 0)) * 100) / 100;
  const voided = isVoided(sale);
  const edited = isEdited(sale);
  const lastEdit = sale.edits?.[sale.edits.length - 1];
  const editState = tillEditState(sale);
  const pendingCancel = voided ? null : pendingCancelRequestOf(sale);
  const refusedCancel = voided || pendingCancel ? null : refusedCancelRequestOf(sale);

  const withdraw = async () => {
    if (!onEdited) return;
    setWithdrawing(true);
    setError(null);
    try {
      const updated = await posApi.withdrawCancelRequest(sale.id);
      setConfirmWithdraw(false);
      onEdited(updated);
    } catch (err) {
      setError(errorMessage(err, 'Could not withdraw the request.'));
    } finally {
      setWithdrawing(false);
    }
  };

  const run = async (what: 'print' | 'pdf') => {
    setBusy(what);
    setError(null);
    try {
      if (what === 'print') {
        await printReceipt(sale.id, 'thermal');
      } else {
        await downloadReceipt(sale.id, billNumberOf(sale), 'a4');
      }
    } catch (err) {
      // The sale is already committed and the stock already moved, so a print
      // failure is never fatal — say so plainly and leave the bill on screen
      // to try again from.
      setError(errorMessage(err, 'Could not produce the receipt.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Bill ${billNumberOf(sale)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="invoice" id="invoice-print">
          <header className="inv-head">
            <h2>{STORE_NAME}</h2>
            <p>{STORE_ADDRESS}</p>
            {STORE_PHONE && <p>Ph: {STORE_PHONE}</p>}
            {STORE_GSTIN && <p>GSTIN: {STORE_GSTIN}</p>}
          </header>

          {voided && <p className="inv-void">*** CANCELLED ***</p>}

          {/* Waiting on an admin. The money is already back with the
              customer, so this is the first thing to read on the bill. */}
          {pendingCancel && (
            <div className="inv-cancel-req">
              <strong>Cancel requested — waiting for an admin</strong>
              <span>
                {pendingCancel.requestedBy?.fullName ?? 'The counter'} ·{' '}
                {billDateTime(pendingCancel.createdAt)}
              </span>
              <span>Reason: {pendingCancel.reason}</span>
              <span>{money(amount(pendingCancel.amount))} handed back to the customer.</span>
            </div>
          )}

          {/* The admin kept the bill after the money had gone back, so staff
              need to see what they said to do about it. */}
          {refusedCancel && (
            <div className="inv-cancel-refused">
              <strong>
                Cancel refused by {refusedCancel.decidedBy?.fullName ?? 'an admin'}
              </strong>
              {refusedCancel.decisionNote && <span>{refusedCancel.decisionNote}</span>}
            </div>
          )}

          {/* Said on the bill itself, not tucked into a corner: somebody
              holding the original receipt is looking at different numbers from
              these, and that is the first thing they need to know. */}
          {edited && !voided && (
            <p className="inv-edited">
              Edited {lastEdit ? billDateTime(lastEdit.createdAt) : ''}
              {lastEdit ? ` · was ${money(amount(lastEdit.totalBefore))}` : ''}
            </p>
          )}

          <dl className="inv-meta">
            <div>
              <dt>Bill</dt>
              <dd>{billNumberOf(sale)}</dd>
            </div>
            <div>
              <dt>Date</dt>
              <dd>{billDateTime(sale.createdAt)}</dd>
            </div>
            <div>
              <dt>Payment</dt>
              <dd>{paymentSummary(sale) ?? '—'}</dd>
            </div>
          </dl>

          <table className="inv-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Qty</th>
                <th className="num">Rate</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id ?? item.bookId ?? i}>
                  <td>
                    <span className="inv-title">{itemTitleOf(item)}</span>
                    {itemCodeOf(item) && <span className="inv-code">{itemCodeOf(item)}</span>}
                  </td>
                  <td className="num">{item.quantity}</td>
                  <td className="num">{money(itemUnitPriceOf(item))}</td>
                  <td className="num">{money(itemTotalOf(item))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Laid out the same way the printed receipt is, so staff
                  checking this against the paper in their hand are reading the
                  same three rows in the same order. */}
              {allOff > 0 && (
                <>
                  <tr className="inv-sub">
                    <td colSpan={3}>Items</td>
                    <td className="num">{money(saleSubtotalOf(sale))}</td>
                  </tr>
                  {coupon && (
                    <tr className="inv-sub">
                      <td colSpan={3}>Coupon {coupon.code}</td>
                      <td className="num">− {money(coupon.amount)}</td>
                    </tr>
                  )}
                  {discount > 0 && (
                    <tr className="inv-sub">
                      <td colSpan={3}>Discount</td>
                      <td className="num">− {money(discount)}</td>
                    </tr>
                  )}
                </>
              )}
              <tr>
                <td colSpan={3}>Total</td>
                <td className="num">{money(saleTotalOf(sale))}</td>
              </tr>
            </tfoot>
          </table>

          {/* Under the total, where the printed receipt carries them too. Only
              on bills the customer agreed to give them for — on screen so a
              mistyped number is caught while they are still at the counter. */}
          {(sale.customerName || sale.customerPhone) && (
            <div className="inv-customer">
              {sale.customerName && <span>Customer: {sale.customerName}</span>}
              {sale.customerPhone && <span>Mobile: {sale.customerPhone}</span>}
            </div>
          )}

          <footer className="inv-foot">
            <p>Thank you for shopping with {STORE_NAME}.</p>
          </footer>
        </div>

        {error && <p className="inv-error">{error}</p>}

        {/* Why the button is missing, said where the button would have been.
            "An admin can" is something staff can act on with a customer in
            front of them; a greyed-out button they cannot press is not. */}
        {onEdited && !editState.allowed && editState.blockedBecause && (
          <p className="muted small inv-edit-blocked">{editState.blockedBecause}</p>
        )}

        {confirmWithdraw && pendingCancel && (
          <div className="cancel-req-withdraw">
            <span>
              Withdraw the request? Put {money(amount(pendingCancel.amount))} back in the drawer
              if the customer kept the books.
            </span>
            <button
              type="button"
              className="btn"
              onClick={() => setConfirmWithdraw(false)}
              disabled={withdrawing}
            >
              No
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void withdraw()}
              disabled={withdrawing}
            >
              {withdrawing ? 'Withdrawing…' : 'Yes, withdraw'}
            </button>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          {onEdited && editState.allowed && can('pos.bills.edit') && (
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Edit bill
            </button>
          )}
          {/* Offered wherever editing is — the screens that can take the
              changed bill back — and on any bill not already cancelled. */}
          {onEdited && !voided && !pendingCancel && can('pos.bills.cancel_request') && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setRequestingCancel(true)}
            >
              Request cancel
            </button>
          )}
          {onEdited && pendingCancel && !confirmWithdraw && can('pos.bills.cancel_request') && (
            <button type="button" className="btn" onClick={() => setConfirmWithdraw(true)}>
              Withdraw request
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={() => void run('pdf')}
            disabled={busy !== null}
          >
            {busy === 'pdf' ? 'Preparing…' : 'Save PDF'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void run('print')}
            disabled={busy !== null}
          >
            {busy === 'print' ? 'Sending…' : 'Print receipt'}
          </button>
        </div>
      </div>

      {requestingCancel && onEdited && (
        <RequestCancel
          sale={sale}
          onCancel={() => setRequestingCancel(false)}
          onSent={(updated) => {
            setRequestingCancel(false);
            onEdited(updated);
          }}
        />
      )}

      {editing && onEdited && (
        <EditBill
          sale={sale}
          onCancel={() => setEditing(false)}
          onSaved={(updated) => {
            setEditing(false);
            // Straight back to the bill, now showing the edited figures, so
            // the receipt staff hand over is the one they just agreed. The
            // list behind is refreshed by whoever owns it.
            onEdited(updated);
          }}
        />
      )}
    </div>
  );
}
